// admin/src/pages/GatewayMonitoring/components/GatewayEventsTable.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { listEvents, exportEventsCsv, extractGatewayErrorMessage } from '../gatewayApi';

function getLevelChipSx(level, mode) {
  const norm = String(level || '').toLowerCase();
  switch (norm) {
    case 'error':
    case 'security':
      return {
        color: mode === 'dark' ? '#FF6B72' : '#991B1B',
        bgcolor: mode === 'dark' ? 'rgba(214, 50, 58, 0.18)' : '#FEE2E2',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      };
    case 'audit':
    case 'warn':
    case 'warning':
      return {
        color: mode === 'dark' ? '#FFC24B' : '#92400E',
        bgcolor: mode === 'dark' ? 'rgba(240, 162, 2, 0.18)' : '#FEF3C7',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      };
    case 'access':
      return {
        color: mode === 'dark' ? '#A78BFA' : '#5B21B6',
        bgcolor: mode === 'dark' ? 'rgba(139, 92, 246, 0.18)' : '#EDE9FE',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      };
    case 'info':
      return {
        color: mode === 'dark' ? '#60A5FA' : '#1E40AF',
        bgcolor: mode === 'dark' ? 'rgba(59, 130, 246, 0.18)' : '#DBEAFE',
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      };
    case 'debug':
    default:
      return {
        color: mode === 'dark' ? '#93A4BD' : '#4B5563',
        bgcolor: mode === 'dark' ? 'rgba(107, 122, 144, 0.18)' : '#F3F4F6',
        fontWeight: 600,
        textTransform: 'uppercase',
        fontSize: '0.7rem',
      };
  }
}

function EventRow({ event, mode, gatewayMap }) {
  const [open, setOpen] = useState(false);
  const gwName = (gatewayMap && gatewayMap[event.gatewayId]) || event.gatewayId || '—';
  const hasDetails = event.fields && Object.keys(event.fields).length > 0;

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 48, p: 0.5 }}>
          {hasDetails && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
          {event.timestamp ? new Date(event.timestamp).toLocaleString() : '—'}
        </TableCell>
        <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
          {gwName}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={event.level || 'info'}
            sx={getLevelChipSx(event.level, mode)}
          />
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
          {event.event || '—'}
        </TableCell>
        <TableCell sx={{ fontSize: '0.82rem' }}>
          {event.outcome ? (
            <Chip
              size="small"
              variant="outlined"
              label={event.outcome}
              color={event.outcome === 'success' ? 'success' : event.outcome.includes('fail') ? 'error' : 'default'}
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
          {event.requestId ? event.requestId.slice(0, 16) : '—'}
        </TableCell>
      </TableRow>

      {/* Expandable Details Row rendered as pure text / JSON */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, p: 2, borderRadius: 1, bgcolor: mode === 'dark' ? '#111827' : '#F9FAFB', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Event Payload & Fields (Sanitized):
              </Typography>
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  fontSize: '0.78rem',
                  fontFamily: 'Consolas, monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  color: mode === 'dark' ? '#E5E7EB' : '#1F2937',
                }}
              >
                {JSON.stringify(event.fields, null, 2)}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function GatewayEventsTable({
  gateways = [],
  mode = 'light',
  canRead = true,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Filters
  const [selectedGateway, setSelectedGateway] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState('all');
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [timePreset, setTimePreset] = useState('24h');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const gatewayMap = React.useMemo(() => {
    const map = {};
    for (const gw of gateways) {
      map[gw._id] = gw.name;
    }
    return map;
  }, [gateways]);

  const getTimeRangeDates = () => {
    const now = new Date();
    if (timePreset === '1h') {
      return { from: new Date(now.getTime() - 3600 * 1000).toISOString(), to: now.toISOString() };
    }
    if (timePreset === '24h') {
      return { from: new Date(now.getTime() - 24 * 3600 * 1000).toISOString(), to: now.toISOString() };
    }
    if (timePreset === '7d') {
      return { from: new Date(now.getTime() - 7 * 24 * 3600 * 1000).toISOString(), to: now.toISOString() };
    }
    if (timePreset === 'custom') {
      return {
        from: customFrom ? new Date(customFrom).toISOString() : undefined,
        to: customTo ? new Date(customTo).toISOString() : undefined,
      };
    }
    return {};
  };

  const fetchEventsData = async (cursor = null, append = false) => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const times = getTimeRangeDates();
      const params = {
        gatewayId: selectedGateway || undefined,
        level: selectedLevel !== 'all' ? selectedLevel : undefined,
        event: selectedEvent.trim() || undefined,
        outcome: selectedOutcome !== 'all' ? selectedOutcome : undefined,
        requestId: requestIdFilter.trim() || undefined,
        from: times.from,
        to: times.to,
        limit: 50,
        cursor: cursor || undefined,
      };

      const res = await listEvents(params);
      if (res && res.data) {
        setEvents((prev) => (append ? [...prev, ...res.data] : res.data));
        setNextCursor(res.pagination?.nextCursor || null);
        setHasMore(Boolean(res.pagination?.hasMore));
      }
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventsData();
  }, [selectedGateway, selectedLevel, selectedOutcome, timePreset]);

  const handleApplyFilters = () => {
    fetchEventsData(null, false);
  };

  const handleResetFilters = () => {
    setSelectedGateway('');
    setSelectedLevel('all');
    setSelectedEvent('');
    setSelectedOutcome('all');
    setRequestIdFilter('');
    setTimePreset('24h');
    setCustomFrom('');
    setCustomTo('');
  };

  const handleLoadMore = () => {
    if (nextCursor && !loading) {
      fetchEventsData(nextCursor, true);
    }
  };

  const handleExportCsv = async () => {
    if (!canRead) return;
    setExportingCsv(true);
    try {
      const times = getTimeRangeDates();
      const params = {
        gatewayId: selectedGateway || undefined,
        level: selectedLevel !== 'all' ? selectedLevel : undefined,
        event: selectedEvent.trim() || undefined,
        outcome: selectedOutcome !== 'all' ? selectedOutcome : undefined,
        requestId: requestIdFilter.trim() || undefined,
        from: times.from,
        to: times.to,
      };

      const res = await exportEventsCsv(params);
      // Create blob and download link
      const blob = new Blob([res], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'gateway-events.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }} data-testid="gateway-events-table-panel">
      {/* Title & CSV Export */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Log & Audit Events
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Live stream of authentication audits, security incidents, access telemetry, and operational errors shipped by gateways.
          </Typography>
        </Box>

        {canRead && (
          <Tooltip title="Exports up to 10,000 matching rows as CSV (sanitized against CSV injection)">
            <Button
              variant="outlined"
              size="small"
              data-testid="export-csv-btn"
              startIcon={exportingCsv ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleExportCsv}
              disabled={exportingCsv}
            >
              Export CSV
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Filter Bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2.5,
          bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-gw-label">Gateway</InputLabel>
              <Select
                labelId="filter-gw-label"
                value={selectedGateway}
                label="Gateway"
                onChange={(e) => setSelectedGateway(e.target.value)}
              >
                <MenuItem value="">All Gateways</MenuItem>
                {gateways.map((gw) => (
                  <MenuItem key={gw._id} value={gw._id}>
                    {gw.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-level-label">Level</InputLabel>
              <Select
                labelId="filter-level-label"
                value={selectedLevel}
                label="Level"
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="access">Access</MenuItem>
                <MenuItem value="audit">Audit</MenuItem>
                <MenuItem value="security">Security</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="debug">Debug</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Event Name"
              value={selectedEvent}
              placeholder="e.g. auth_success"
              onChange={(e) => setSelectedEvent(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-outcome-label">Outcome</InputLabel>
              <Select
                labelId="filter-outcome-label"
                value={selectedOutcome}
                label="Outcome"
                onChange={(e) => setSelectedOutcome(e.target.value)}
              >
                <MenuItem value="all">All Outcomes</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="invalid_credentials">Invalid Credentials</MenuItem>
                <MenuItem value="locked">Locked</MenuItem>
                <MenuItem value="directory_unavailable">Directory Unavailable</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-time-label">Time Window</InputLabel>
              <Select
                labelId="filter-time-label"
                value={timePreset}
                label="Time Window"
                onChange={(e) => setTimePreset(e.target.value)}
              >
                <MenuItem value="1h">Last 1 Hour</MenuItem>
                <MenuItem value="24h">Last 24 Hours</MenuItem>
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={1.6}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyFilters}
                startIcon={<FilterListIcon />}
                fullWidth
              >
                Filter
              </Button>
              <Tooltip title="Reset filters">
                <IconButton size="small" onClick={handleResetFilters}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>

          {timePreset === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Events Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Gateway</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Outcome</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Request ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Loading events...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No matching log or audit events found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((ev) => (
                <EventRow
                  key={ev._id || `${ev.gatewayId}-${ev.seq}`}
                  event={ev}
                  mode={mode}
                  gatewayMap={gatewayMap}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cursor Pagination Button */}
      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Loading...' : 'Load More Events'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
